import { useState, useRef, useEffect, useMemo, KeyboardEvent, type CSSProperties } from 'react';
import React from 'react'; // Added missing import
import { useGraph, useSimulationStore } from '../../../store/simulationStore';
import { BASE_UNITS } from '../../../utils/unitConstants';
import { isValidUnit, simplifyUnit, extractUnitIdentifiers, isUnitDefined, validateUnitIdentifier } from '../../../utils/units';

interface FormulaInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  currentNodeId?: string;
  rows?: number;
  ariaLabel?: string;
  expandOnFocus?: boolean;
}

type AutocompleteMode = 'node' | 'unit' | null;
const FORMULA_INPUT_MIN_HEIGHT = 26;
const FORMULA_INPUT_MAX_EXPANDED_HEIGHT = 156;

export const FormulaInput = React.forwardRef<HTMLTextAreaElement, FormulaInputProps>(
  ({ value, onChange, onBlur, onFocus, currentNodeId, rows = 4, ariaLabel, expandOnFocus = false }, ref) => {
    const graph = useGraph();
    const customUnits = useSimulationStore((state) => state.graph.customUnits || []);
    const addCustomUnit = useSimulationStore((state) => state.addCustomUnit);
    
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>(null);
    const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [filterText, setFilterText] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

    React.useImperativeHandle(ref, () => internalRef.current!);

    // Get available nodes (excluding self)
    const availableNodes = useMemo(
      () => graph.nodes.filter((node) => node.id !== currentNodeId),
      [graph.nodes, currentNodeId]
    );

    // Filter nodes based on current input
    const filteredNodes = useMemo(() => {
      if (!filterText) {
        return availableNodes;
      }

      const lower = filterText.toLowerCase();
      return availableNodes.filter((node) => node.data.name.toLowerCase().includes(lower));
    }, [availableNodes, filterText]);

    // Filter units based on current input
    const filteredUnits = useMemo((): Array<{ value: string; description: string; type: 'base' | 'custom' | 'create' }> => {
      // Merge custom units with base units
      const customUnitSuggestions = customUnits.map(unit => ({
        value: unit,
        description: 'custom unit',
        type: 'custom' as const
      }));
      
      const allUnits: Array<{ value: string; description: string; type: 'base' | 'custom' }> = [
        ...BASE_UNITS.map(u => ({ ...u, type: 'base' as const })), 
        ...customUnitSuggestions
      ];
      
      if (!filterText) {
        return allUnits;
      }

      const searchTerm = filterText.toLowerCase().trim();
      const operatorPattern = /[\s*/^]+/;
      const parts = filterText.split(operatorPattern);
      const lastPart = parts[parts.length - 1]?.trim() || '';
      const lastPartLower = lastPart.toLowerCase();
      const filterTerm = lastPartLower || searchTerm;

      let filtered: Array<{ value: string; description: string; type: 'base' | 'custom' | 'create' }> = allUnits.filter(
        (unit) =>
          unit.value.toLowerCase().includes(filterTerm) ||
          unit.description.toLowerCase().includes(filterTerm)
      );
      
      // Check if we should show "Create custom unit" option
      if (lastPart && filterText) {
        const exactMatch = allUnits.some(u => u.value.toLowerCase() === lastPartLower);
        if (!exactMatch && !isUnitDefined(lastPart)) {
          const validation = validateUnitIdentifier(lastPart);
          if (validation.valid) {
            // Add "Create custom unit" option at the top
            filtered = [
              {
                value: lastPart,
                description: `Create custom unit "${lastPart}"`,
                type: 'create' as const
              },
              ...filtered
            ];
          }
        }
      }
      
      return filtered;
    }, [filterText, customUnits]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          autocompleteRef.current &&
          !autocompleteRef.current.contains(event.target as Node) &&
          internalRef.current &&
          !internalRef.current.contains(event.target as Node)
        ) {
          setShowAutocomplete(false);
          setFilterText('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    React.useLayoutEffect(() => {
      if (!expandOnFocus) {
        return;
      }

      const textarea = internalRef.current;
      if (!textarea) {
        return;
      }

      if (!isFocused) {
        textarea.style.height = `${FORMULA_INPUT_MIN_HEIGHT}px`;
        textarea.style.overflowY = 'hidden';
        return;
      }

      textarea.style.height = 'auto';
      const nextHeight = Math.min(
        Math.max(textarea.scrollHeight, FORMULA_INPUT_MIN_HEIGHT),
        FORMULA_INPUT_MAX_EXPANDED_HEIGHT
      );
      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > FORMULA_INPUT_MAX_EXPANDED_HEIGHT ? 'auto' : 'hidden';
    }, [expandOnFocus, isFocused, value]);

    useEffect(() => {
      if (!showAutocomplete) {
        return;
      }

      const items = autocompleteMode === 'unit' ? filteredUnits : filteredNodes;
      
      if (items.length === 0) {
        setShowAutocomplete(false);
        setFilterText('');
        setAutocompleteMode(null);
        return;
      }

      if (selectedIndex >= items.length) {
        setSelectedIndex(0);
        return;
      }

      const currentItem = itemRefs.current[selectedIndex];
      currentItem?.scrollIntoView({ block: 'nearest' });
    }, [filteredNodes, filteredUnits, selectedIndex, showAutocomplete, autocompleteMode]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      const textarea = internalRef.current;
      if (!textarea) return;

      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = newValue.substring(0, cursorPosition);

      // Check for node reference pattern: ${"...
      const nodeMatch = textBeforeCursor.match(/\$\{\s*(?:["'])?([^"'{}]*)$/);
      if (nodeMatch) {
        setFilterText(nodeMatch[1]);
        setAutocompleteMode('node');
        setShowAutocomplete(true);
        setSelectedIndex(0);

        const { offsetTop, offsetLeft, clientHeight } = textarea;
        setAutocompletePosition({
          top: offsetTop + clientHeight,
          left: offsetLeft
        });
        return;
      }

      // Check for unit expression pattern
      // Only trigger when:
      // 1. After operators like "/", "*", "^" followed by optional space
      // 2. Or when typing unit identifiers that match known base units
      // 3. Or when typing standalone unit names (not inside node references)
      
      // Make sure we're not inside a node reference ${"...}
      const lastDollarBrace = textBeforeCursor.lastIndexOf('${');
      const lastClosingBrace = textBeforeCursor.lastIndexOf('}');
      const isInNodeReference = lastDollarBrace > lastClosingBrace;
      
      // Pattern 1: After operator (e.g., "kg / ", "EUR * ", "m ^ ")
      const afterOperatorPattern = /[\s*/^]+\s*([a-zA-Z0-9_^]*)$/;
      const afterOperatorMatch = textBeforeCursor.match(afterOperatorPattern);
      
      if (afterOperatorMatch && !isInNodeReference) {
        const filterTerm = afterOperatorMatch[1].trim();
        setFilterText(filterTerm);
        setAutocompleteMode('unit');
        setShowAutocomplete(true);
        setSelectedIndex(0);

        const { offsetTop, offsetLeft, clientHeight } = textarea;
        setAutocompletePosition({
          top: offsetTop + clientHeight,
          left: offsetLeft
        });
        return;
      }
      
      // Pattern 2: Unit expression with operators (e.g., "kg / ha", "EUR * g")
      // Only match if it contains operators and looks like a unit expression
      const unitExpressionPattern = /([a-zA-Z][a-zA-Z0-9_^]*\s*[\s*/^]+\s*[a-zA-Z0-9_^]*)$/;
      const unitExpressionMatch = textBeforeCursor.match(unitExpressionPattern);
      
      if (unitExpressionMatch && !isInNodeReference) {
        const potentialUnit = unitExpressionMatch[1].trim();
        // Extract the part after the last operator for filtering
        const operatorPattern = /[\s*/^]+/;
        const parts = potentialUnit.split(operatorPattern);
        const lastPart = parts[parts.length - 1]?.trim() || '';
        const filterTerm = lastPart || potentialUnit;
        
        // Only show if it looks like a valid unit expression (has operators)
        if (parts.length > 1) {
          setFilterText(filterTerm);
          setAutocompleteMode('unit');
          setShowAutocomplete(true);
          setSelectedIndex(0);

          const { offsetTop, offsetLeft, clientHeight } = textarea;
          setAutocompletePosition({
            top: offsetTop + clientHeight,
            left: offsetLeft
          });
          return;
        }
      }
      
      // Pattern 3: Standalone unit name (e.g., user typing "kg", "EUR", etc.)
      // Only show if it looks like a unit identifier and we're not in a node reference
      // Also check that the text before doesn't look like part of a variable or expression
      const standaloneUnitPattern = /([a-zA-Z][a-zA-Z0-9_^]*)$/;
      const standaloneMatch = textBeforeCursor.match(standaloneUnitPattern);
      
      if (standaloneMatch && !isInNodeReference) {
        const potentialUnit = standaloneMatch[1].trim();
        const beforeMatch = textBeforeCursor.substring(0, textBeforeCursor.length - potentialUnit.length);
        
        // Don't show if it looks like part of a variable name or expression
        // (e.g., "price" or "value * 2" shouldn't trigger unit autocomplete)
        const looksLikeVariable = /[a-zA-Z0-9_]\s*$/.test(beforeMatch) && !/[\s*/^+-]\s*$/.test(beforeMatch);
        
        if (!looksLikeVariable && potentialUnit.length > 0) {
          // Check if it could be a unit (matches the beginning of any base unit)
          const matchesBaseUnit = BASE_UNITS.some(unit => 
            unit.value.toLowerCase().startsWith(potentialUnit.toLowerCase()) ||
            potentialUnit.toLowerCase().startsWith(unit.value.toLowerCase())
          );
          
          if (matchesBaseUnit) {
            setFilterText(potentialUnit);
            setAutocompleteMode('unit');
            setShowAutocomplete(true);
            setSelectedIndex(0);

            const { offsetTop, offsetLeft, clientHeight } = textarea;
            setAutocompletePosition({
              top: offsetTop + clientHeight,
              left: offsetLeft
            });
            return;
          }
        }
      }

      // No match - hide autocomplete
      setShowAutocomplete(false);
      setFilterText('');
      setAutocompleteMode(null);
    };

    const insertNodeName = (nodeName: string) => {
      const textarea = internalRef.current;
      if (!textarea) return;

      const currentValue = textarea.value;
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      const textBeforeCursor = currentValue.substring(0, selectionStart);
      const textAfterCursor = currentValue.substring(selectionEnd);

      const lastDollarBraceIndex = textBeforeCursor.lastIndexOf('${');
      if (lastDollarBraceIndex === -1) return;

      const beforeDollarBrace = currentValue.substring(0, lastDollarBraceIndex);
      const insertedValue = `\${"${nodeName}"}`;
      const newValue = `${beforeDollarBrace}${insertedValue}${textAfterCursor}`;

      textarea.value = newValue;
      onChange(newValue);
      setShowAutocomplete(false);
      setFilterText('');
      setAutocompleteMode(null);

      requestAnimationFrame(() => {
        const newCursorPos = beforeDollarBrace.length + insertedValue.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      });
    };

    const insertUnit = (unitValue: string, isCreateAction: boolean = false) => {
      // If this is a "create" action, first create the custom unit
      if (isCreateAction) {
        const result = addCustomUnit(unitValue);
        if (!result.success) {
          // Silently fail for formula input - the unit won't be created but the text will still be inserted
          console.warn('Failed to create custom unit:', result.error);
        }
      }
      
      const textarea = internalRef.current;
      if (!textarea) return;

      const currentValue = textarea.value;
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      const textBeforeCursor = currentValue.substring(0, selectionStart);
      const textAfterCursor = currentValue.substring(selectionEnd);

      // Pattern 1: After operator (e.g., "kg / " -> "kg / ha")
      const afterOperatorPattern = /([\s*/^]+\s*)([a-zA-Z0-9_^]*)$/;
      const afterOperatorMatch = textBeforeCursor.match(afterOperatorPattern);
      
      if (afterOperatorMatch) {
        const operatorPart = afterOperatorMatch[1];
        const partialText = afterOperatorMatch[2]; // The text after operator that should be replaced
        const beforeOperator = textBeforeCursor.substring(0, textBeforeCursor.length - afterOperatorMatch[0].length);
        
        // Check if there's a unit before the operator to form a compound unit
        const beforeOperatorMatch = beforeOperator.match(/([a-zA-Z][a-zA-Z0-9_^]*)\s*$/);
        if (beforeOperatorMatch) {
          // We have a compound unit like "kg / ha"
          const firstUnit = beforeOperatorMatch[1];
          const fullUnitExpression = `${firstUnit} ${operatorPart.trim()} ${unitValue}`;
          const simplified = simplifyUnit(fullUnitExpression);
          const beforeUnit = beforeOperator.substring(0, beforeOperator.length - beforeOperatorMatch[1].length);
          const newValue = `${beforeUnit}${simplified}${textAfterCursor}`;

          textarea.value = newValue;
          onChange(newValue);
          setShowAutocomplete(false);
          setFilterText('');
          setAutocompleteMode(null);

          requestAnimationFrame(() => {
            const newCursorPos = beforeUnit.length + simplified.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            textarea.focus();
          });
          return;
        } else {
          // Just replace the partial text after operator
          const newValue = `${beforeOperator}${operatorPart.trim()} ${unitValue}${textAfterCursor}`;
          textarea.value = newValue;
          onChange(newValue);
          setShowAutocomplete(false);
          setFilterText('');
          setAutocompleteMode(null);

          requestAnimationFrame(() => {
            const newCursorPos = beforeOperator.length + operatorPart.trim().length + 1 + unitValue.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            textarea.focus();
          });
          return;
        }
      }
      
      // Pattern 2: Unit expression with operators (e.g., "kg / ha" -> replace "ha" part)
      const unitExpressionPattern = /([a-zA-Z][a-zA-Z0-9_^]*\s*[\s*/^]+\s*)([a-zA-Z0-9_^]*)$/;
      const unitExpressionMatch = textBeforeCursor.match(unitExpressionPattern);
      
      if (unitExpressionMatch) {
        const beforeLastPart = textBeforeCursor.substring(0, textBeforeCursor.length - unitExpressionMatch[0].length);
        const operatorPart = unitExpressionMatch[1];
        const lastPart = unitExpressionMatch[2]; // The part to replace
        
        // Replace the last part with the selected unit
        const newUnitExpression = `${beforeLastPart}${operatorPart.trim()} ${unitValue}`;
        const simplified = simplifyUnit(newUnitExpression.trim());
        const newValue = `${simplified}${textAfterCursor}`;

        textarea.value = newValue;
        onChange(newValue);
        setShowAutocomplete(false);
        setFilterText('');
        setAutocompleteMode(null);

        requestAnimationFrame(() => {
          const newCursorPos = simplified.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          textarea.focus();
        });
        return;
      }
      
      // Pattern 3: Standalone unit name (e.g., user typed "kg" and selects "kg")
      // Match a word that could be a unit (not part of a node reference)
      const standaloneUnitPattern = /([a-zA-Z][a-zA-Z0-9_^]*)$/;
      const standaloneMatch = textBeforeCursor.match(standaloneUnitPattern);
      
      // Make sure we're not inside a node reference ${"...}
      const lastDollarBrace = textBeforeCursor.lastIndexOf('${');
      const lastClosingBrace = textBeforeCursor.lastIndexOf('}');
      const isInNodeReference = lastDollarBrace > lastClosingBrace;
      
      if (standaloneMatch && !isInNodeReference) {
        const matchedText = standaloneMatch[1];
        const beforeMatch = textBeforeCursor.substring(0, textBeforeCursor.length - matchedText.length);
        
        // Replace the matched text with the selected unit
        const newValue = `${beforeMatch}${unitValue}${textAfterCursor}`;
        textarea.value = newValue;
        onChange(newValue);
        setShowAutocomplete(false);
        setFilterText('');
        setAutocompleteMode(null);

        requestAnimationFrame(() => {
          const newCursorPos = beforeMatch.length + unitValue.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          textarea.focus();
        });
        return;
      }
      
      // No match - just insert the unit
      const newValue = `${textBeforeCursor}${unitValue}${textAfterCursor}`;
      textarea.value = newValue;
      onChange(newValue);
      setShowAutocomplete(false);
      setFilterText('');
      setAutocompleteMode(null);

      requestAnimationFrame(() => {
        const newCursorPos = selectionStart + unitValue.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      });
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showAutocomplete) return;

      const items = autocompleteMode === 'unit' ? filteredUnits : filteredNodes;
      if (items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % items.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + items.length) % items.length);
          break;
        case 'Enter':
          if (!e.shiftKey) {
            e.preventDefault();
            if (autocompleteMode === 'unit') {
              const unit = filteredUnits[selectedIndex];
              insertUnit(unit.value, unit.type === 'create');
            } else {
              insertNodeName(filteredNodes[selectedIndex].data.name);
            }
          }
          break;
        case 'Tab':
          if (!e.shiftKey) {
            e.preventDefault();
            if (autocompleteMode === 'unit') {
              const unit = filteredUnits[selectedIndex];
              insertUnit(unit.value, unit.type === 'create');
            } else {
              insertNodeName(filteredNodes[selectedIndex].data.name);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowAutocomplete(false);
          setFilterText('');
          setAutocompleteMode(null);
          break;
      }
    };

    const handleTextareaBlur = () => {
      setIsFocused(false);

      // Auto-add custom units from the formula expression
      if (value && value.trim()) {
        const identifiers = extractUnitIdentifiers(value);
        
        for (const identifier of identifiers) {
          // Skip if it's already defined (base or custom unit)
          if (isUnitDefined(identifier)) {
            continue;
          }
          
          // Try to add as custom unit (silently, no error display in formula input)
          addCustomUnit(identifier);
        }
      }
      
      setShowAutocomplete(false);
      setFilterText('');
      setAutocompleteMode(null);
      onBlur?.();
    };

    const focusTextarea = () => {
      setIsFocused(true);
      internalRef.current?.focus();
    };

    const items = autocompleteMode === 'unit' ? filteredUnits : filteredNodes;
    itemRefs.current.length = items.length;

    return (
      <div
        className="formula-input-wrapper"
        onMouseDown={(event) => {
          event.stopPropagation();
          focusTextarea();
        }}
      >
        <textarea
          ref={internalRef}
          rows={rows}
          wrap={expandOnFocus ? (isFocused ? 'soft' : 'off') : undefined}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={handleTextareaBlur}
          onClick={(e) => {
            e.stopPropagation();
            focusTextarea();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            focusTextarea();
          }}
          placeholder='Use ${"variable name"} to reference nodes, or type units like "kg / ha"'
          className={`formula-input-textarea${expandOnFocus ? ' formula-input-textarea-expandable' : ''}${expandOnFocus && isFocused ? ' is-expanded' : ''}`}
          aria-label={ariaLabel}
        />
        
        {showAutocomplete && items.length > 0 && (
          <div
            ref={autocompleteRef}
            className="formula-autocomplete"
            style={{
              '--formula-autocomplete-top': `${autocompletePosition.top}px`,
              '--formula-autocomplete-left': `${autocompletePosition.left}px`
            } as CSSProperties}
            onWheel={(e) => {
              // Prevent wheel events from propagating to the graph
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // Prevent blur when clicking on suggestions
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {autocompleteMode === 'unit' ? (
              filteredUnits.map((unit, index) => (
                <div
                  key={unit.value}
                  className={`formula-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertUnit(unit.value, unit.type === 'create');
                  }}
                >
                  {unit.type === 'create' ? (
                    <>
                      <div className="formula-autocomplete-create-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontFamily: 'Monaco, Courier New, monospace', color: 'var(--color-text-primary)' }}>
                          {unit.value}
                        </div>
                        <div className="formula-autocomplete-create-description" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                          {unit.description}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="autocomplete-icon">📏</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontFamily: 'Monaco, Courier New, monospace', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {unit.value}
                          {unit.type === 'custom' && (
                            <span style={{ 
                              fontSize: '0.65rem', 
                              padding: '1px 4px', 
                              borderRadius: '3px', 
                              backgroundColor: 'var(--color-accent, #6366f1)', 
                              color: 'white',
                              fontWeight: 500
                            }}>
                              custom
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                          {unit.description}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))
            ) : (
              filteredNodes.map((node, index) => (
                <div
                  key={node.id}
                  className={`formula-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertNodeName(node.data.name);
                  }}
                >
                  <span className="autocomplete-icon">
                    {node.data.type === 'distribution' ? '📊' : '🧮'}
                  </span>
                  <span className="autocomplete-name">{node.data.name}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }
);
